import { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { crmStore, useCrm } from '../store';
import type { Client, TypeClient } from '../types';
import { Search, Loader2, Building2, User as UserIcon, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import PiecesJointes from './PiecesJointes';
import SearchableMultiSelect from './SearchableMultiSelect';
import AddressAutocomplete from './AddressAutocomplete';
import CityAutocomplete from './CityAutocomplete';
import { extractClientPrefs, buildClientTags } from '../utils/clientPrefs';

const SOURCE_SUGGESTIONS = [
  'Mariages.net', 'Bouche-à-oreille', 'Appel entrant', 'Site web', 'Google',
  'Réseaux sociaux', 'Instagram', 'Facebook', 'Recommandation pro',
  'Salon professionnel', 'Mariages.com', 'Zankyou', 'Partenaire', 'Autre',
];

const SEGMENT_SUGGESTIONS = [
  'Wedding', 'Corporate', 'Anniversaire', 'Baptême', 'Cocktail', 'Soirée privée',
  'Festival', 'Inauguration', 'Séminaire', 'Team building', 'Arbre de Noël',
  'Cérémonie laïque', 'Salon professionnel', 'Institutionnel',
];


const TYPES: { value: TypeClient; label: string }[] = [
  { value: 'particulier', label: 'Particulier' },
  { value: 'entreprise', label: 'Entreprise' },
  { value: 'collectivite', label: 'Collectivité publique' },
  { value: 'association', label: 'Association' },
];

const empty = (): Client => ({
  id: '',
  typeClient: 'particulier',
  nom: '',
  prenom: '',
  societe: '',
  fonction: '',
  email: '',
  tel: '',
  telSecondaire: '',
  adresse: '',
  cp: '',
  ville: '',
  departement: '',
  pays: 'France',
  siret: '',
  tvaIntra: '',
  source: '',
  segment: '',
  tags: [],
  potentielBusiness: '',
  notes: '',
});

export default function ClientForm({
  open, onOpenChange, initial, onSaved,
}: { open: boolean; onOpenChange: (v: boolean) => void; initial: Client | null; onSaved?: (client: Client) => void }) {
  const data = useCrm();
  const [c, setC] = useState<Client>(empty());
  const [searching, setSearching] = useState(false);
  const [verified, setVerified] = useState(false);
  const [domainesPref, setDomainesPref] = useState<string[]>([]);
  const [artistesPref, setArtistesPref] = useState<string[]>([]);

  useEffect(() => {
    if (open) {
      const base = initial ? { ...initial } : empty();
      setC(base);
      setVerified(!!initial?.siret);
      const prefs = extractClientPrefs(base.tags);
      setDomainesPref(prefs.domaines);
      setArtistesPref(prefs.artistes);
    }
  }, [open, initial]);

  // Domaines disponibles : référentiel global du CRM
  const domainesOptions = useMemo(() => {
    const set = new Set<string>(data.domaines || []);
    (data.prest || []).forEach((p) => (p.domaines || []).forEach((d) => set.add(d)));
    return Array.from(set).sort();
  }, [data.domaines, data.prest]);

  const set = <K extends keyof Client>(k: K, v: Client[K]) => setC((p) => ({ ...p, [k]: v }));
  const togglePref = (list: string[], setList: (v: string[]) => void, value: string) => {
    setList(list.includes(value) ? list.filter((x) => x !== value) : [...list, value]);
  };

  // Recherche entreprise via API publique gouv (pas de clé)
  const searchSiren = async () => {
    const q = (c.siret || c.societe || '').trim();
    if (!q) { toast.error('SIRET ou raison sociale requis'); return; }
    setSearching(true);
    try {
      const url = `https://recherche-entreprises.api.gouv.fr/search?q=${encodeURIComponent(q)}&page=1&per_page=1`;
      const res = await fetch(url);
      const json = await res.json();
      const r = json?.results?.[0];
      if (!r) { toast.error('Aucune entreprise trouvée'); return; }
      const siege = r.siege || {};
      setC((p) => ({
        ...p,
        typeClient: r.nature_juridique?.startsWith('92') ? 'association' : 'entreprise',
        societe: r.nom_complet || r.nom_raison_sociale || p.societe,
        siret: siege.siret || p.siret,
        tvaIntra: r.tva_intracommunautaire || p.tvaIntra,
        adresse: siege.adresse || p.adresse,
        cp: siege.code_postal || p.cp,
        ville: siege.libelle_commune || p.ville,
        departement: siege.departement || p.departement,
      }));
      setVerified(true);
      toast.success(`✓ ${r.nom_complet} — vérifié auprès de l'INSEE`);
    } catch (e) {
      console.error(e);
      toast.error('Erreur API recherche entreprises');
    } finally {
      setSearching(false);
    }
  };

  const handleSave = async () => {
    if (!c.nom.trim() && !c.societe?.trim()) {
      toast.error('Nom ou société obligatoire');
      return;
    }
    const toSave = { ...c };
    if (!toSave.nom.trim() && toSave.societe) toSave.nom = toSave.societe;
    // Réinjecte les préférences (domaines/artistes) dans le champ tags
    const others = extractClientPrefs(toSave.tags).others;
    toSave.tags = buildClientTags(others, domainesPref, artistesPref);
    const saved = await crmStore.upsertClient(toSave);
    toast.success('Client enregistré');
    onOpenChange(false);
    if (saved && onSaved) onSaved(saved);
  };

  const isPro = c.typeClient !== 'particulier';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-slate-950 border-slate-800">
        <DialogHeader>
          <DialogTitle className="font-heading text-amber-400 flex items-center gap-2">
            {isPro ? <Building2 size={18} strokeWidth={1.5} /> : <UserIcon size={18} strokeWidth={1.5} />}
            {initial ? 'Modifier client' : 'Nouveau client'}
          </DialogTitle>
        </DialogHeader>

        {/* Type */}
        <div className="flex gap-2 flex-wrap">
          {TYPES.map((t) => (
            <button
              key={t.value}
              onClick={() => set('typeClient', t.value)}
              className={`px-3 py-1.5 rounded-lg text-xs border ${
                c.typeClient === t.value
                  ? 'bg-amber-500/15 text-amber-400 border-amber-500/40'
                  : 'bg-slate-900 text-slate-400 border-slate-800 hover:bg-slate-800'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Bloc Pro : SIRET + recherche INSEE */}
        {isPro && (
          <div className="border border-slate-800 bg-slate-900/40 rounded-lg p-4 space-y-3">
            <div className="text-[10px] text-slate-400 uppercase tracking-widest flex items-center justify-between">
              <span>🏢 Identification entreprise (API INSEE — gratuite)</span>
              {verified && <span className="flex items-center gap-1 text-emerald-400 normal-case tracking-normal text-xs"><CheckCircle2 size={12} /> Vérifié INSEE</span>}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Field label="SIRET" value={c.siret || ''} onChange={(v) => { set('siret', v); setVerified(false); }} placeholder="14 chiffres" />
              <Field label="N° TVA Intra" value={c.tvaIntra || ''} onChange={(v) => set('tvaIntra', v)} placeholder="FRXX..." />
              <button
                onClick={searchSiren}
                disabled={searching}
                className="self-end px-3 py-2 bg-emerald-500/15 hover:bg-emerald-500/25 border border-emerald-500/40 text-emerald-300 rounded-lg text-xs flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {searching ? <Loader2 size={13} className="animate-spin" /> : <Search size={13} strokeWidth={1.5} />}
                Rechercher INSEE
              </button>
            </div>
            <Field label="Raison sociale" value={c.societe || ''} onChange={(v) => set('societe', v)} placeholder="Nom de l'entreprise" />
          </div>
        )}

        {/* Identité contact */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label={isPro ? 'Contact – Nom' : 'Nom *'} value={c.nom} onChange={(v) => set('nom', v)} />
          <Field label={isPro ? 'Contact – Prénom' : 'Prénom'} value={c.prenom || ''} onChange={(v) => set('prenom', v)} />
          {isPro && <Field label="Fonction" value={c.fonction || ''} onChange={(v) => set('fonction', v)} placeholder="DRH, Resp. com..." />}
          <Field label="Email" value={c.email || ''} onChange={(v) => set('email', v)} type="email" />
          <Field label="Téléphone" value={c.tel || ''} onChange={(v) => set('tel', v)} />
          <Field label="Téléphone secondaire" value={c.telSecondaire || ''} onChange={(v) => set('telSecondaire', v)} />
        </div>

        {/* Adresse */}
        <div className="border border-slate-800 bg-slate-900/40 rounded-lg p-4 space-y-3">
          <div className="text-[10px] text-slate-400 uppercase tracking-widest">📍 Adresse (autocomplétion BAN — gratuit)</div>
          <label className="block">
            <span className="text-[10px] text-slate-400 uppercase tracking-widest mb-1 block">Rue</span>
            <AddressAutocomplete
              value={c.adresse || ''}
              onChange={(v) => set('adresse', v)}
              onPick={(s) => {
                setC((p) => ({
                  ...p,
                  adresse: [s.housenumber, s.street].filter(Boolean).join(' ') || s.label,
                  cp: s.postcode || p.cp,
                  ville: s.city || p.ville,
                  // s.context = "10, Aube, Grand Est" → on prend le 2e élément (nom du département)
                  departement: s.context?.split(',')[1]?.trim() || p.departement,
                  pays: 'France',
                }));
                toast.success('Adresse complétée');
              }}
              placeholder="Tapez : 8 rue du Champ, Saint-Pouange…"
            />
          </label>
          <div className="text-[10px] text-slate-500 italic">Pas d'adresse précise ? Renseignez juste la ville ci-dessous (autocomplétion).</div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <Field label="CP" value={c.cp || ''} onChange={(v) => set('cp', v)} />
            <label className="block">
              <span className="text-[10px] text-slate-400 uppercase tracking-widest mb-1 block">Ville</span>
              <CityAutocomplete
                value={c.ville || ''}
                onChange={(v) => set('ville', v)}
                onPick={(s) => {
                  setC((p) => ({
                    ...p,
                    ville: s.city || s.label,
                    cp: s.postcode || p.cp,
                    departement: s.context?.split(',')[1]?.trim() || p.departement,
                    pays: 'France',
                  }));
                  toast.success('Ville complétée');
                }}
              />
            </label>
            <Field label="Département" value={c.departement || ''} onChange={(v) => set('departement', v)} />
            <Field label="Pays" value={c.pays || 'France'} onChange={(v) => set('pays', v)} />
          </div>
        </div>

        {/* Suivi commercial */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <FieldWithList
            label="Source"
            value={c.source || ''}
            onChange={(v) => set('source', v)}
            placeholder="Mariages.net, Bouche-à-oreille…"
            listId="client-source-suggestions"
            options={SOURCE_SUGGESTIONS}
          />
          <Select label="Potentiel business" value={c.potentielBusiness || ''} onChange={(v) => set('potentielBusiness', v)} options={[
            { value: '', label: '—' },
            { value: 'Faible', label: 'Faible' },
            { value: 'Moyen', label: 'Moyen' },
            { value: 'Élevé', label: 'Élevé' },
            { value: 'VIP', label: 'VIP' },
          ]} />
        </div>

        {/* Préférences artistiques */}
        <div className="border border-slate-800 bg-slate-900/40 rounded-lg p-4 space-y-3">
          <div className="text-[10px] text-slate-400 uppercase tracking-widest">🎭 Préférences artistiques</div>
          <div className="grid grid-cols-1 gap-3">
            <SearchableMultiSelect
              label="Domaines artistiques"
              options={domainesOptions}
              selected={domainesPref}
              onToggle={(v) => togglePref(domainesPref, setDomainesPref, v)}
              placeholder="DJ, Magicien, Saxophoniste…"
            />
          </div>
        </div>

        <label className="block">
          <span className="text-[10px] text-slate-400 uppercase tracking-widest mb-1 block">Notes internes</span>
          <textarea
            value={c.notes || ''}
            onChange={(e) => set('notes', e.target.value)}
            rows={3}
            className="w-full px-3 py-2 bg-slate-900/60 border border-slate-800 rounded-lg text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-amber-500/50"
            placeholder="Préférences, historique relationnel, alertes..."
          />
        </label>

        <div className="flex justify-end gap-2 pt-2">
          <button onClick={() => onOpenChange(false)} className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg text-sm">Annuler</button>
          <button onClick={handleSave} className="px-4 py-2 bg-amber-500 hover:bg-amber-400 text-slate-950 rounded-lg text-sm font-medium">Enregistrer</button>
        </div>
        {initial?.id && <PiecesJointes entityType="client" entityId={initial.id} />}
      </DialogContent>
    </Dialog>
  );
}

function Field(props: { label: string; value: string; onChange: (v: string) => void; placeholder?: string; type?: string }) {
  return (
    <label className="block">
      <span className="text-[10px] text-slate-400 uppercase tracking-widest mb-1 block">{props.label}</span>
      <input
        type={props.type || 'text'}
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        placeholder={props.placeholder}
        className="w-full px-3 py-2 bg-slate-900/60 border border-slate-800 rounded-lg text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-amber-500/50"
      />
    </label>
  );
}

function FieldWithList(props: { label: string; value: string; onChange: (v: string) => void; placeholder?: string; listId: string; options: string[] }) {
  return (
    <label className="block">
      <span className="text-[10px] text-slate-400 uppercase tracking-widest mb-1 block">{props.label}</span>
      <input
        type="text"
        list={props.listId}
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        placeholder={props.placeholder}
        className="w-full px-3 py-2 bg-slate-900/60 border border-slate-800 rounded-lg text-sm text-slate-200 placeholder-slate-600 focus:outline-none focus:border-amber-500/50"
      />
      <datalist id={props.listId}>
        {props.options.map((o) => <option key={o} value={o} />)}
      </datalist>
    </label>
  );
}


function Select({ label, value, onChange, options }: { label: string; value: string; onChange: (v: string) => void; options: { value: string; label: string }[] }) {
  return (
    <label className="block">
      <span className="text-[10px] text-slate-400 uppercase tracking-widest mb-1 block">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 bg-slate-900/60 border border-slate-800 rounded-lg text-sm text-slate-200 focus:outline-none focus:border-amber-500/50"
      >
        {options.map((o) => <option key={o.value} value={o.value} className="bg-slate-950">{o.label}</option>)}
      </select>
    </label>
  );
}
